'use client'

import { ROUTES } from '@/core/constants/routes'
import { capitalizeFirstLetter } from '@/core/lib/utils/capitalize-first-letter'
import { cn } from '@/core/lib/utils/cn'
import { NavLink } from '@/core/ui/atoms/nav-link'
import { Link } from '@/i18n/navigation'
import { Accordion, AccordionItem } from '@heroui/accordion'
import { ChevronRight } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { useParams } from 'next/navigation'
import { useEffect } from 'react'
import { HeadlineModel } from '../../../types/models/headline.model'
import { useBlogSidebarStore } from './store'

type HeadlineListProps = {
  headlines: HeadlineModel[]
}

export const HeadlineList: React.FC<HeadlineListProps> = ({ headlines }) => {
  const t = useTranslations()
  const params = useParams()
  const headline = params?.headline?.toString() || ''
  const { activeHeadline, setActiveHeadline } = useBlogSidebarStore()

  useEffect(() => {
    setActiveHeadline(new Set([headline]))
  }, [headline, setActiveHeadline])

  const isSelected = (key: string) => {
    if (activeHeadline === 'all') return true
    if (activeHeadline instanceof Set) return activeHeadline.has(key)
    return false
  }
  if (!headlines.length) {
    return (
      <div className='flex flex-col items-center gap-3 p-3'>
        <p>{t('blog.noHeadlines')}</p>
      </div>
    )
  }
  return (
    <Accordion
      selectedKeys={activeHeadline}
      onSelectionChange={setActiveHeadline}
      showDivider={false}
    >
      {headlines.map(headline => (
        <AccordionItem
          key={headline.slug}
          indicator={({ isOpen }) => (
            <ChevronRight
              className={cn('text-navy cursor-pointer', isOpen && 'rotate-180')}
            />
          )}
          title={
            <Link
              onClick={e => e.stopPropagation()}
              href={ROUTES.headline(headline.slug)}
              className={cn(
                'font-medium',
                isSelected(headline.slug) && 'text-red',
              )}
            >
              {headline.name} {params?.headline}
            </Link>
          }
        >
          <ul className='flex list-inside list-disc flex-col gap-2'>
            {headline.posts.map(post => (
              <li key={post.id} className='ps-2'>
                <NavLink
                  href={ROUTES.post(post.slug)}
                  className={{
                    main: 'hover:text-red transition-colors',
                  }}
                >
                  {capitalizeFirstLetter(post.name)}
                </NavLink>
              </li>
            ))}
          </ul>
        </AccordionItem>
      ))}
    </Accordion>
  )
}
